import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/lib/env';
import { STORAGE_KEYS, TIMING } from '@/config/constants';

const api: AxiosInstance = axios.create({
  baseURL: `${env.REACT_APP_API_BASE_URL}/api/v1`,
  timeout: TIMING.API_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error)
);

type SessionExpiredCallback = () => void;
let onSessionExpired: SessionExpiredCallback | null = null;

/**
 * Register a callback invoked when the API receives a 401.
 * Designed to be called once from a React component that has access to
 * the router (e.g., App.tsx), so we can navigate via React Router
 * instead of a hard page reload.
 */
export function registerSessionExpiredHandler(handler: SessionExpiredCallback) {
  onSessionExpired = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      if (onSessionExpired && !url.startsWith('/auth/')) {
        onSessionExpired();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
